import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import EntityHubLayout, { PAGE_SIZE } from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import EditProductDialog from "../../components/product-manager/EditProductDialog";
import BulkEntityUploadDialog from "../../components/product-manager/BulkEntityUploadDialog";
import { PRODUCT_BULK_CONFIG } from "../../utils/bulkUpload/bulkEntityConfigs";
import {
  filterByHubState,
  useBookmarkableHubState,
} from "../../hooks/useBookmarkableHubState";
import { useProducts } from "../../hooks/useProducts";
import { apiService } from "../../api/services/apiService";
import type { Product } from "../../types";
import { EntityStatus } from "../../types";
import { pmHubAddPath, PM_HUBS } from "../../utils/pmHubRoutes";
import { useAuth } from "../../contexts/AuthContext";

const ProductHubPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const hubState = useBookmarkableHubState({ extraParamKeys: ["approval"] });
  const { products: fetched, loading, error, refresh } = useProducts();
  const [products, setProducts] = useState<Product[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);
  const currentUserEmail = user?.email ?? null;

  useEffect(() => {
    setProducts(fetched);
  }, [fetched]);

  const filtered = useMemo(() => {
    let list = filterByHubState(products, hubState);
    if (hubState.approval === true) {
      list = list.filter((p) => p.approvalNeeded === true);
    } else if (hubState.approval === false) {
      list = list.filter((p) => !p.approvalNeeded);
    }
    return list;
  }, [products, hubState]);

  const paginated = useMemo(() => {
    const start = (hubState.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, hubState.page]);

  const editProduct = useMemo(
    () => products.find((p) => p.uuid === hubState.editUuid) ?? null,
    [products, hubState.editUuid],
  );

  const handleToggle = async (product: Product) => {
    setToggleLoadingId(product.uuid);
    const newStatus =
      product.status === EntityStatus.ACTIVE
        ? EntityStatus.INACTIVE
        : EntityStatus.ACTIVE;
    try {
      const updated = await apiService.updateProduct(product.uuid, {
        name: product.name,
        description: product.description ?? undefined,
        status: newStatus,
        approvalNeeded: product.approvalNeeded,
      });
      setProducts((prev) =>
        prev.map((p) => (p.uuid === updated.uuid ? updated : p)),
      );
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  return (
    <>
      <EntityHubLayout
        hubTitle="Products"
        hubDescription="Manage products with individual and bulk create, update, and enable or disable."
        addPath={pmHubAddPath("product")}
        addLabel="Add Product"
        onBulkUpload={() => setBulkOpen(true)}
        loading={loading}
        error={error}
        itemCount={filtered.length}
        hubState={hubState}
        filterSidebarExtra={
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Approval needed
            </Typography>
            <RadioGroup
              value={
                hubState.approval === null
                  ? "all"
                  : hubState.approval
                    ? "yes"
                    : "no"
              }
              onChange={(e) => {
                const v = e.target.value;
                hubState.setApproval(
                  v === "all" ? null : v === "yes",
                );
              }}
            >
              <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
            </RadioGroup>
          </Box>
        }
      >
        <Grid container spacing={2}>
          {paginated.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.uuid}>
              <EntityAssetCard
                title={product.name}
                subtitle={product.description ?? undefined}
                status={product.status}
                metadata={
                  <>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Subscribable: {product.subscribable ? "Yes" : "No"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Approval: {product.approvalNeeded ? "Required" : "Not required"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Linked: {product.apis?.length ?? 0} APIs,{" "}
                      {product.categories?.length ?? 0} categories,{" "}
                      {product.faqs?.length ?? 0} FAQs,{" "}
                      {product.documents?.length ?? 0} documents,{" "}
                      {product.links?.length ?? 0} links
                    </Typography>
                  </>
                }
                enabled={product.status === EntityStatus.ACTIVE}
                toggleLoading={toggleLoadingId === product.uuid}
                onToggleEnabled={() => handleToggle(product)}
                onEdit={() => hubState.setEditUuid(product.uuid)}
                onDelete={async () => {
                  await apiService.deleteProduct(product.uuid);
                  setProducts((prev) =>
                    prev.filter((p) => p.uuid !== product.uuid),
                  );
                }}
                onViewRequests={
                  currentUserEmail &&
                  product.productOwners?.includes(currentUserEmail)
                    ? () =>
                        navigate("/subscription-requests", {
                          state: {
                            productUuid: product.uuid,
                            returnPath: PM_HUBS.product,
                            returnSearch: hubState.searchString,
                          },
                        })
                    : undefined
                }
              />
            </Grid>
          ))}
          {!loading && filtered.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No products match your filters.
              </Typography>
            </Grid>
          )}
        </Grid>
      </EntityHubLayout>

      <BulkEntityUploadDialog
        open={bulkOpen}
        config={PRODUCT_BULK_CONFIG}
        onClose={() => setBulkOpen(false)}
        onComplete={() => refresh()}
      />

      <EditProductDialog
        product={editProduct}
        open={Boolean(hubState.editUuid && editProduct)}
        onClose={() => hubState.setEditUuid(null)}
        onSaved={(updated) =>
          setProducts((prev) =>
            prev.map((p) => (p.uuid === updated.uuid ? updated : p)),
          )
        }
      />
    </>
  );
};

export default ProductHubPage;
